import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { tripAPI, popularRouteAPI, promoAPI, waitingListAPI } from '../../services/api';
import VehicleTypeBadge from '../../components/VehicleTypeBadge';
import AppNavbar from '../../components/AppNavbar';
import SiteFooter from '../../components/SiteFooter';
import MobileBottomNav from '../../components/MobileBottomNav';
import { TermsModal, PrivacyPolicyModal, RefundPolicyModal } from '../../components/legal/LegalModals';
import { CitySelect, ArabicDatePicker } from '../../components/SearchWidgets';
import { arNum, arPrice } from '../../utils/arFormat';
import { whatsappUrl } from '../../config/whatsapp';
import {
  BusFront, CarFront, Package, Truck, Headphones, Zap, Tag, Smartphone,
  User, Building2, Snowflake, Wifi, BatteryCharging, Tv,
  Pencil, ArrowLeftRight, Search, ChevronLeft, Clock, MapPin, Flag, Ticket,
  AlertTriangle, Gift, Lightbulb, CheckCircle2, Wallet, Armchair, Check,
} from 'lucide-react';

/* Bus hero image — swap this URL for your own hosted photo */
const BUS_IMAGE_URL = '/bus-hero-v3.png';

const API_HOST = (process.env.REACT_APP_API_URL || '').replace(/\/api$/, '');
const resolveUrl = (url) => (!url ? '' : url.startsWith('http') ? url : `${API_HOST}${url}`);

/* Extra query-string params to carry booking type/group name through to the
   book page — empty for 'individual' so its URL/behavior is unchanged. */
const bookingTypeQuery = (bookingType, groupName) => {
  if (!bookingType || bookingType === 'individual') return '';
  let q = `&bookingType=${encodeURIComponent(bookingType)}`;
  if (groupName?.trim()) q += `&groupName=${encodeURIComponent(groupName.trim())}`;
  return q;
};

/* ─────────────────────── Arabic date formatter (DD / MM / YYYY) ──────── */
const formatArabicDate = (dateStr) => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${arNum(d)} / ${arNum(m)} / ${arNum(y)}`;
};

/* ─────────────────────── Round-trip pairing ─────────────────────── */
const pairTrips = (outbounds, returns) => {
  const used = new Set();
  const pairs = [];
  for (const out of outbounds) {
    let ret = returns.find(r => r.companyId === out.companyId && !used.has(r.id));
    if (!ret) ret = returns.find(r => !used.has(r.id));
    if (ret) used.add(ret.id);
    pairs.push({ out, ret: ret || null, sameCompany: !!ret && ret.companyId === out.companyId });
  }
  for (const ret of returns) {
    if (!used.has(ret.id)) pairs.push({ out: null, ret, sameCompany: false });
  }
  pairs.sort((a, b) => {
    const aC = a.out && a.ret ? 1 : 0;
    const bC = b.out && b.ret ? 1 : 0;
    if (bC !== aC) return bC - aC;
    return (b.sameCompany ? 1 : 0) - (a.sameCompany ? 1 : 0);
  });
  return pairs;
};

/* ═══════════════════════════════════════════════════════════
   FeatureCard
═══════════════════════════════════════════════════════════ */
const FeatureCard = ({ Icon, title, subtitle, accent, iconColor }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_2px_12px_rgba(10,77,157,.06)] p-4 sm:p-5 flex flex-col items-center text-center gap-2 hover:shadow-[0_6px_24px_rgba(10,77,157,.10)] hover:-translate-y-0.5 transition-all duration-200">
    <div className={`w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${accent} ${iconColor}`}>
      <Icon size={22} strokeWidth={2} />
    </div>
    <div>
      <p className="font-bold text-gray-800 text-xs sm:text-sm">{title}</p>
      <p className="text-[11px] sm:text-xs text-gray-600 mt-0.5 leading-snug">{subtitle}</p>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════
   HeroSection — headline + trip-search booking area on the right (RTL
   primary reading position), bus photo on the left, roughly 50/50. The
   search form used to float in a separate card below/overlapping the
   hero via a negative margin — merged into one block here so the actual
   booking task is immediately visible beside the image, not the image
   alone with the form implied further down.
═══════════════════════════════════════════════════════════ */
const HeroSection = ({ children }) => (
  <div dir="rtl" className="relative overflow-hidden bg-gradient-to-b from-[#EAF1FF] via-[#F1F6FF] to-[#F5F9FF]">
    <div className="relative max-w-7xl mx-auto flex flex-col lg:flex-row items-start gap-3 lg:gap-8 px-4 sm:px-6 lg:px-10 pt-3 sm:pt-5 lg:pt-6 pb-5 sm:pb-8 lg:pb-8">

      {/* ── Booking column (right in RTL) — headline, supporting line, then
           the actual search form: mobile order matches DOM order (this
           block first, image second), so search controls are reachable
           immediately without scrolling past a big photo first. Tightened
           further (pt-6 not pt-8 on desktop, smaller headline/subtitle
           gaps) so بحث عن رحلة sits inside a normal desktop first
           viewport instead of needing a scroll to reach it. ── */}
      <div className="flex-1 w-full lg:max-w-[560px] z-10">
        <h1 className="font-black leading-tight tracking-tight text-2xl sm:text-3xl lg:text-[38px] text-[#0F2354] text-center lg:text-right">
          رحلتك تبدأ من هنا
        </h1>
        <p className="text-gray-600 text-sm sm:text-base mt-1 mb-2 sm:mb-3 text-center lg:text-right">
          احجز رحلتك بين المدن بسهولة وأمان
        </p>
        {children}
      </div>

      {/* ── Bus photo — shorter on mobile so it never outweighs the
           booking form (the actual task) that now sits above it in DOM
           order. ── */}
      <div className="flex-1 w-full max-w-xl lg:max-w-none">
        <div className="relative rounded-[28px] overflow-hidden shadow-[0_20px_50px_rgba(15,35,84,.18)] h-32 sm:h-64 lg:h-[460px]">
          <img
            src={BUS_IMAGE_URL}
            alt=""
            // object-right-top (not object-center/object-top): this photo has
            // "معك في كل طريق" / "معا نقرب المسافات" text baked into its
            // top-right corner. On desktop the container is narrower than
            // the image at this height, so it crops horizontally too —
            // object-top alone still let center-anchored horizontal cropping
            // slice through the right-side text. Anchoring both axes to the
            // top-right guarantees that corner is never cropped, on any
            // breakpoint; only the left/bottom (bridge/skyline, no text)
            // gets cropped instead.
            className="absolute inset-0 w-full h-full object-cover object-right-top"
            loading="eager"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      </div>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════
   ServiceTabBar — the 4-way "حجز رحلة / مشاوير / إرسال طرد / نقل عام"
   switcher living at the top of the floating search card.
═══════════════════════════════════════════════════════════ */
const SERVICE_TABS = [
  { key: 'trip',      Icon: BusFront, label: 'حجز رحلة' },
  { key: 'rides',     Icon: CarFront, label: 'مشاوير'   },
  { key: 'parcel',    Icon: Package,  label: 'إرسال طرد' },
  { key: 'transport', Icon: Truck,    label: 'نقل عام'   },
];

const ServiceTabBar = ({ active, onChange }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-4">
    {SERVICE_TABS.map((t) => (
      // Outer wrapper carries a brushed-metal gradient (light→dark→light,
      // like a chrome bevel) that peeks through a 2px gap as the button's
      // "border" — more reliable across browsers than border-image. Text
      // uses near-black/white (not mid-tone grays) so it stays legible
      // even washed out by direct sunlight. py-4 keeps mobile height in the
      // ~56-64px comfortable-but-compact target range.
      <div
        key={t.key}
        className={`relative p-[2px] rounded-2xl transition-all ${
          active === t.key
            ? 'bg-gradient-to-b from-[#A9BCF2] via-[#2748B8] to-[#0F1B4D]'
            : 'bg-gradient-to-b from-gray-100 via-gray-400 to-gray-200'
        }`}
      >
        <button
          type="button"
          onClick={() => onChange(t.key)}
          className={`w-full flex items-center justify-center gap-2 py-4 rounded-[14px] text-sm sm:text-base font-black tracking-tight transition-all ${
            active === t.key
              ? 'bg-[#1E40AF] text-white shadow-[0_4px_14px_rgba(30,64,175,.35)]'
              : 'bg-white text-[#0B1440] hover:bg-blue-50/50'
          }`}
        >
          <t.Icon size={24} strokeWidth={2} className="flex-shrink-0" /> {t.label}
        </button>
        {/* Extra, unmistakable "this one is selected" signal beyond just
            the fill color — a checkmark badge in the corner. */}
        {active === t.key && (
          <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#FFC928] border-2 border-white flex items-center justify-center shadow-sm">
            <Check size={11} strokeWidth={3} className="text-[#0F1B4D]" />
          </span>
        )}
      </div>
    ))}
  </div>
);

/* Non-trip tabs don't have an inline form of their own (that would mean
   maintaining three more booking flows in parallel with the dedicated pages
   that already do this fully) — clicking one navigates straight to the real
   page where the fields actually live, no intermediate card/second click. */
const SERVICE_TARGETS = {
  rides: '/rides/new',
  parcel: '/parcels/new',
  transport: '/transport/new',
};

/* ═══════════════════════════════════════════════════════════
   ServicesGridSection — "خدمات سافر" 4-card grid
   Was baked-text PNG images (public/UI/*.png) — replaced with real SVG
   icon + title + description cards: legible at any size, translatable,
   and consistent with the icon family used everywhere else on this page.
   حجز رحلة (the core Safer service) gets a light-blue tint + stronger
   border/icon treatment; the other three stay plain white so the accent
   doesn't turn into a rainbow of category colors.
═══════════════════════════════════════════════════════════ */
const SAFER_SERVICES = [
  {
    key: 'trip', Icon: BusFront, title: 'حجز رحلة', desc: 'رحلات بين المدن', target: null, primary: true,
    details: 'احجز تذكرتك لأي مدينة في السودان بسهولة وأمان — اختر رحلتك، ادفع إلكترونياً، واستلم تذكرتك فوراً على هاتفك.',
  },
  {
    key: 'rides', Icon: CarFront, title: 'مشاوير', desc: 'تنقل داخل المدينة', target: '/rides/new',
    details: 'اطلب مشواراً داخل مدينتك — سيارة أو ركشة حسب حاجتك. حدد نقطة الانطلاق والوجهة، وسيرسل لك السائقون القريبون عروض أسعارهم.',
  },
  {
    key: 'parcel', Icon: Package, title: 'إرسال طرد', desc: 'إرسال ومتابعة الطرود', target: '/parcels/new',
    details: 'لديك تذكرة سفر؟ أرسل طردك معها إلى مدينة وجهتك، وتابع حالته من لحظة الإرسال حتى الاستلام برقم تتبع خاص بك.',
  },
  {
    key: 'transport', Icon: Truck, title: 'نقل عام', desc: 'نقل الحمولات والمعدات', target: '/transport/new',
    details: 'احجز مركبة لنقل البضائع والمعدات والحمولات الكبيرة بين المدن — شاحنات وبيكب ودفار حسب حجم حمولتك.',
  },
];

const ServiceGridCard = ({ Icon, title, desc, primary, onClick }) => (
  <button
    onClick={onClick}
    className={`group text-right rounded-2xl border p-4 sm:p-5 transition-all hover:-translate-y-0.5 ${
      primary
        ? 'bg-blue-50/70 border-[#1E40AF]/25 shadow-[0_4px_18px_rgba(30,64,175,.12)] hover:border-[#1E40AF]/45 hover:shadow-[0_8px_26px_rgba(30,64,175,.18)]'
        : 'bg-white border-gray-100 shadow-[0_2px_10px_rgba(10,77,157,.06)] hover:border-blue-100 hover:shadow-[0_8px_24px_rgba(10,77,157,.10)]'
    }`}
  >
    <div className={`w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center mb-3 transition-transform group-hover:translate-x-[-2px] ${
      primary ? 'bg-[#1E40AF] text-white' : 'bg-blue-50 text-[#1E40AF]'
    }`}>
      <Icon size={primary ? 26 : 24} strokeWidth={2} />
    </div>
    <p className="font-black text-gray-900 text-[15px] sm:text-base">{title}</p>
    <p className="text-xs sm:text-sm text-gray-500 mt-1 leading-snug">{desc}</p>
  </button>
);

// Cards open an info popup first (brief description + متابعة button) rather
// than navigating immediately — onCardClick hands the whole service object
// up to the page, which owns the popup state and the actual navigation.
const ServicesGridSection = ({ onCardClick }) => (
  <div id="safer-services" className="scroll-mt-24">
    <div className="text-center mb-5">
      <h2 className="text-xl sm:text-2xl font-black text-gray-800">خدمات سافر</h2>
      <p className="text-sm text-gray-600 mt-1">كل احتياجات تنقلك وشحنتك في مكان واحد</p>
    </div>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      {SAFER_SERVICES.map((s) => (
        <ServiceGridCard key={s.key} {...s} onClick={() => onCardClick(s)} />
      ))}
    </div>
  </div>
);

// Not one of the 4 SAFER_SERVICES, but opens the same info popup pattern —
// target '/bookings' matches the ServiceInfoModal's generic "navigate to
// target on متابعة" handling used for the service cards.
const TICKET_INFO = {
  key: 'ticket', Icon: Ticket, title: 'تذكرتك الإلكترونية', target: '/bookings',
  details: 'احجز وادفع واستلم تذكرتك مباشرة على هاتفك — بدون طباعة. تابع كل حجوزاتك في أي وقت من صفحة حجوزاتي.',
};

/* ═══════════════════════════════════════════════════════════
   ServiceInfoModal — brief description + متابعة, opened by the خدمات سافر
   cards and تذكرتك الإلكترونية instead of navigating immediately.
═══════════════════════════════════════════════════════════ */
const ServiceInfoModal = ({ info, onClose, onContinue }) => {
  if (!info) return null;
  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center" dir="rtl">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white w-full sm:max-w-sm sm:rounded-2xl rounded-t-[28px] shadow-2xl p-6" style={{ animation: 'slideDown 0.22s ease both' }}>
        <button onClick={onClose} className="absolute left-4 top-4 text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        <div className="w-14 h-14 rounded-2xl bg-[#1E40AF] text-white flex items-center justify-center mb-4">
          <info.Icon size={26} strokeWidth={2} />
        </div>
        <h3 className="font-black text-lg text-gray-900 mb-2">{info.title}</h3>
        <p className="text-sm text-gray-600 leading-relaxed mb-6">{info.details}</p>
        <button
          onClick={onContinue}
          className="w-full bg-[#1E40AF] hover:bg-[#1D4ED8] text-white font-bold py-3 rounded-xl transition-all"
        >
          متابعة
        </button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   BottomFeatureCards — تذكرتك الإلكترونية / المساعدة
   (the "اكتشف وجهات جديدة" card was removed per request)
═══════════════════════════════════════════════════════════ */
const BottomFeatureCards = ({ onTicketClick }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
    <button onClick={onTicketClick} className="text-right bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all p-5 flex items-center gap-4">
      <div className="w-14 h-14 rounded-xl bg-blue-50 flex items-center justify-center text-[#1E40AF] flex-shrink-0"><Ticket size={24} strokeWidth={2} /></div>
      <div className="flex-1 min-w-0">
        <p className="font-black text-gray-800 text-sm mb-1">تذكرتك الإلكترونية</p>
        <p className="text-xs text-gray-500">احجز وادفع واستلم تذكرتك على هاتفك</p>
        <span className="text-xs text-[#1E40AF] font-bold mt-1 inline-block">اعرف المزيد ←</span>
      </div>
    </button>

    <a href={whatsappUrl()} target="_blank" rel="noopener noreferrer" className="rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-all flex items-center">
      <img src="/UI/whatsapp-help.png" alt="نحن هنا للمساعدة" className="w-full h-auto block" />
    </a>
  </div>
);

/* ═══════════════════════════════════════════════════════════
   SearchCard — floating over hero
═══════════════════════════════════════════════════════════ */
const SearchCard = ({
  tripType, setTripType,
  form, setForm,
  cities, citiesLoading,
  loading, onSearch, searchError,
  searched, searchCollapsed, setSearchCollapsed,
  searchedSeats, outboundTrips, roundPairs,
  narrow = false,
}) => {
  const today = new Date().toISOString().split('T')[0];
  const set = (field) => (e) =>
    setForm(f => ({ ...f, [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const swapCities = () => setForm(f => ({ ...f, fromCity: f.toCity, toCity: f.fromCity }));

  if (searched && searchCollapsed) {
    const hasResults = tripType === 'one-way' ? outboundTrips.length > 0 : roundPairs.length > 0;
    return (
      <div className="bg-white rounded-[22px] border border-gray-100 shadow-[0_8px_40px_rgba(10,77,157,.12)] px-5 py-3.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-gray-700 flex-1 min-w-0 flex-wrap">
          <span className="font-bold text-[#1E3A8A]">{form.fromCity || 'أي مدينة'}</span>
          <span className="text-gray-300 font-light">←</span>
          <span className="font-bold text-[#1E3A8A]">{form.toCity || 'أي مدينة'}</span>
          <span className="text-gray-200 hidden sm:inline">|</span>
          <span className="text-gray-500 hidden sm:inline text-xs">{form.date ? formatArabicDate(form.date) : 'كل التواريخ'}</span>
          {tripType === 'round-trip' && form.returnDate && (
            <><span className="text-gray-200 hidden sm:inline">—</span><span className="text-gray-500 hidden sm:inline text-xs">{formatArabicDate(form.returnDate)}</span></>
          )}
          <span className="text-gray-200">|</span>
          <span className="text-gray-700 text-xs font-semibold flex items-center gap-1"><User size={13} strokeWidth={2} /> {arNum(searchedSeats)}</span>
          {hasResults
            ? <span className="bg-emerald-50 text-emerald-700 text-xs px-2.5 py-0.5 rounded-full font-semibold border border-emerald-100">
                {tripType === 'one-way' ? `${arNum(outboundTrips.length)} رحلة` : `${arNum(roundPairs.length)} باقة`}
              </span>
            : <span className="bg-gray-100 text-gray-600 text-xs px-2.5 py-0.5 rounded-full">لا نتائج</span>
          }
        </div>
        <button
          onClick={() => setSearchCollapsed(false)}
          className="flex-shrink-0 text-sm bg-blue-50 hover:bg-blue-100 text-[#1E3A8A] px-4 py-2 rounded-xl font-bold transition-colors border border-blue-100 flex items-center gap-1.5"
        >
          <Pencil size={14} strokeWidth={2} /> تعديل
        </button>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-[20px] shadow-[0_20px_60px_rgba(10,30,82,.15)] border border-gray-100 ${narrow ? 'p-4 sm:p-5' : 'p-6 sm:p-7'}`}>

      {/* ── Trip type toggle + collapse — plain text labels, no directional
           arrows (they read ambiguously in RTL and the selected fill/color
           already communicates the choice on its own). mb-3 (not mb-4) to
           tighten the gap before من on mobile. ── */}
      <div className="flex items-center justify-between mb-3">
        <div className="inline-flex bg-gray-100 rounded-xl p-1 gap-1">
          <button type="button" onClick={() => setTripType('one-way')}
            className={`px-5 py-2 rounded-lg text-sm font-bold transition-all ${
              tripType === 'one-way'
                ? 'bg-[#1E40AF] text-white shadow-[0_3px_10px_rgba(30,64,175,.3)]'
                : 'text-gray-500 hover:text-gray-700'
            }`}>
            ذهاب فقط
          </button>
          <button type="button" onClick={() => setTripType('round-trip')}
            className={`px-5 py-2 rounded-lg text-sm font-bold transition-all ${
              tripType === 'round-trip'
                ? 'bg-[#1E40AF] text-white shadow-[0_3px_10px_rgba(30,64,175,.3)]'
                : 'text-gray-500 hover:text-gray-700'
            }`}>
            ذهاب وعودة
          </button>
        </div>
        {searched && (
          <button type="button" onClick={() => setSearchCollapsed(true)}
            className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 transition-colors">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7"/>
            </svg>
            إخفاء
          </button>
        )}
      </div>

      <form onSubmit={onSearch} noValidate>
        {/* نوع الحجز (فردي/عائلي/مجموعة/شركات) removed from the initial
            search per UX refinement — too many decisions before the actual
            journey. form.bookingType/groupName stay in state (default
            'individual'/''), bookingTypeQuery() and the group-suggestions
            capacity logic in handleSearch are untouched — this only hides
            the selector; the classification can be surfaced again later
            in the booking flow if/when needed, without touching that logic. */}

        {/* ── Main inputs row: من | ⇆ | إلى | التاريخ | الركاب ──
             narrow (hero, ~50% column width) stays at a 2-column stack even
             on large viewports — the 5-column grid needs real width to not
             cram; it only kicks in once the card has the full page width
             (post-search collapsed bar). ── */}
        {/* Two columns even on narrow phones (not just sm: and up) — a
            single stacked column (من/إلى/تاريخ/ركاب as 4 full-width rows)
            made this card tall enough that بحث عن رحلة landed right under
            the fixed mobile bottom nav on first load, looking cut off. */}
        <div className={`grid grid-cols-2 ${narrow ? '' : 'lg:grid-cols-[1.5fr_44px_1.5fr_1fr_1fr]'} gap-3 items-end mb-4`}>

          {/* من */}
          <div>
            <label className="block text-xs font-bold text-gray-600 mb-1.5 mr-0.5">من</label>
            <CitySelect
              value={form.fromCity}
              onChange={city => setForm(f => ({ ...f, fromCity: city }))}
              cities={cities} loadingCities={citiesLoading}
              // Safer blue, not red — red implies an error/warning, which
              // "your starting point" definitely isn't.
              icon={<MapPin size={20} strokeWidth={2} className="text-[#1E40AF]" />}
            />
          </div>

          {/* Swap — hidden on mobile, and hidden entirely in narrow (hero)
              mode: the compact 2-column grid there pairs من/إلى and
              تاريخ/الركاب cleanly, and a 5th cell for this button would
              break that pairing. Desktop (non-narrow, full-width) keeps it. */}
          {!narrow && (
          <div className="hidden lg:flex items-end justify-center pb-0.5">
            <button type="button" onClick={swapCities}
              className="w-10 h-[52px] bg-gray-50 hover:bg-blue-50 hover:text-[#1E40AF] border-2 border-gray-200 hover:border-blue-200 text-gray-400 rounded-xl flex items-center justify-center transition-all"
              title="تبديل المدينتين">
              <ArrowLeftRight size={18} strokeWidth={2} />
            </button>
          </div>
          )}

          {/* إلى */}
          <div>
            <label className="block text-xs font-bold text-gray-600 mb-1.5 mr-0.5">إلى</label>
            <CitySelect
              value={form.toCity}
              onChange={city => setForm(f => ({ ...f, toCity: city }))}
              cities={cities} loadingCities={citiesLoading}
              icon={<Flag size={20} strokeWidth={2} className="text-[#1E3A8A]" />}
            />
          </div>

          {/* تاريخ الرحلة (+ return date on round-trip) */}
          <div>
            <label className="block text-xs font-bold text-gray-600 mb-1.5 mr-0.5">
              {tripType === 'round-trip' ? 'تاريخ الذهاب' : 'تاريخ الرحلة'}
            </label>
            <ArabicDatePicker
              value={form.date}
              onChange={nd => setForm(f => ({ ...f, date: nd, ...(f.returnDate && nd && f.returnDate < nd ? { returnDate: '' } : {}) }))}
              min={today}
              placeholder="أي تاريخ"
              allowClear
            />
          </div>

          {/* عدد الركاب */}
          <div>
            <label className="block text-xs font-bold text-gray-600 mb-1.5 mr-0.5">عدد الركاب</label>
            <div className="flex items-center justify-between border-2 border-gray-200 hover:border-[#1E3A8A]/40 rounded-2xl px-3 h-[52px] bg-white transition-all">
              <button type="button"
                onClick={() => setForm(f => ({ ...f, seats: Math.max(1, f.seats - 1) }))}
                className="w-8 h-8 rounded-xl bg-gray-100 hover:bg-blue-50 hover:text-[#1E40AF] text-gray-600 font-bold text-lg transition-all flex items-center justify-center flex-shrink-0">
                −
              </button>
              <span className="text-sm font-bold text-gray-800 flex items-center gap-1.5">
                <User size={15} strokeWidth={2} className="text-gray-500" />
                <span>{arNum(form.seats)}</span>
                <span className="text-gray-600 font-semibold text-xs">{form.seats === 1 ? 'راكب' : 'ركاب'}</span>
              </span>
              <button type="button"
                onClick={() => setForm(f => ({ ...f, seats: Math.min(f.bookingType === 'group' ? 60 : 9, f.seats + 1) }))}
                className="w-8 h-8 rounded-xl bg-gray-100 hover:bg-blue-50 hover:text-[#1E40AF] text-gray-600 font-bold text-lg transition-all flex items-center justify-center flex-shrink-0">
                +
              </button>
            </div>
          </div>
        </div>

        {/* Return date — shown full-width when round-trip (mobile fallback) */}
        {tripType === 'round-trip' && (
          <div className="mb-4">
            <label className="block text-xs font-bold text-gray-600 mb-1.5 mr-0.5">تاريخ العودة</label>
            <ArabicDatePicker
              value={form.returnDate}
              onChange={nd => setForm(f => ({ ...f, returnDate: nd }))}
              min={form.date || today}
              placeholder="أي تاريخ"
              allowClear
            />
          </div>
        )}

        {/* تكييف/واي فاي/شحن/شاشات filter pills removed from the search
            form — one more decision the passenger doesn't need to make
            before searching. form.hasAC/hasWifi/hasChargers/hasTV stay in
            state (default false) and the filter-building logic in
            handleSearch is untouched; there's just no UI control setting
            them anymore. Each trip's actual amenities already show on its
            result card instead (TripCard badges below), which is a more
            useful place to see this than a pre-search filter. */}

        {searchError && (
          <div className="mb-4 bg-red-50 border border-red-100 text-red-700 text-sm font-semibold rounded-xl px-4 py-3 text-center">
            {searchError}
          </div>
        )}

        {/* CTA button */}
        <button type="submit" disabled={loading}
          className="w-full h-[56px] bg-[#1E40AF] hover:bg-[#1D4ED8] disabled:opacity-60 text-white rounded-xl text-base font-black transition-all shadow-[0_6px_20px_rgba(30,64,175,.3)] hover:shadow-[0_10px_28px_rgba(30,64,175,.4)] hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-3"
        >
          {loading ? (
            <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
            </svg>
          ) : (
            <Search size={20} strokeWidth={2.5} />
          )}
          {loading ? 'جاري البحث عن الرحلات...' : 'بحث عن رحلة'}
        </button>
      </form>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   TripCard — one-way result card
═══════════════════════════════════════════════════════════ */
const TripCard = ({ trip, searchedSeats, navigate, bookingType, groupName }) => {
  const booked  = trip.bookedSeats?.length || 0;
  const total   = (booked + trip.availableSeats) || 1;
  const pct     = Math.round((booked / total) * 100);
  const partial = trip.availableSeats > 0 && trip.availableSeats < searchedSeats;
  const fmt     = (dt) => new Date(dt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
  const fmtD    = (dt) => new Date(dt).toLocaleDateString('ar-SA', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
  const durH    = Math.floor((new Date(trip.arrivalTime) - new Date(trip.departureTime)) / 3600000);
  const durM    = Math.floor(((new Date(trip.arrivalTime) - new Date(trip.departureTime)) % 3600000) / 60000);

  return (
    <div
      className={`bg-white rounded-2xl border transition-all hover:shadow-[0_8px_28px_rgba(10,77,157,.10)] hover:-translate-y-0.5 overflow-hidden ${
        partial ? 'border-amber-200' : 'border-gray-100 shadow-[0_2px_10px_rgba(10,77,157,.06)]'
      }`}
      style={{ animation: 'cardSlideIn 0.2s ease both' }}
    >
      {/* Card top accent line */}
      <div className={`h-1 ${partial ? 'bg-amber-400' : 'bg-gradient-to-l from-[#1E3A8A] to-[#1E40AF]'}`} />

      {/* Header */}
      <div className="px-5 py-3.5 border-b border-gray-50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
            </svg>
          </div>
          <span className="text-sm font-bold text-blue-700">{trip.company?.name}</span>
          <VehicleTypeBadge vehicleType={trip.bus?.vehicleType} />
        </div>
        <span className="text-xs text-gray-700 font-semibold">{fmtD(trip.departureTime)}</span>
      </div>

      {/* Partial warning */}
      {partial && (
        <div className="mx-5 mt-3 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-700 flex items-center gap-2">
          <AlertTriangle size={14} strokeWidth={2} className="flex-shrink-0" />
          <span>متاح {arNum(trip.availableSeats)} مقعد فقط — طلبت {arNum(searchedSeats)}</span>
        </div>
      )}

      {/* Route row */}
      <div className="px-5 py-4 flex items-center gap-4">
        <div className="text-center flex-shrink-0">
          <p className="text-[26px] font-black text-gray-900 tabular-nums leading-none">{arNum(fmt(trip.departureTime))}</p>
          <p className="text-[13px] font-bold text-gray-700 mt-1">{trip.fromCity}</p>
        </div>
        <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
          <div className="flex items-center gap-1 text-gray-300 w-full">
            <div className="w-2 h-2 rounded-full bg-[#1E40AF] flex-shrink-0" />
            <div className="flex-1 border-t-2 border-dashed border-blue-200" />
            <BusFront size={18} strokeWidth={2} className="text-[#1E40AF] flex-shrink-0" />
            <div className="flex-1 border-t-2 border-dashed border-blue-200" />
            <div className="w-2 h-2 rounded-full bg-[#1E40AF] flex-shrink-0" />
          </div>
          <span className="text-[11px] text-blue-600 font-bold">{durH > 0 ? `${arNum(durH)}س ` : ''}{arNum(durM)}د</span>
        </div>
        <div className="text-center flex-shrink-0">
          <p className="text-[26px] font-black text-gray-900 tabular-nums leading-none">{arNum(fmt(trip.arrivalTime))}</p>
          <p className="text-[13px] font-bold text-gray-700 mt-1">{trip.toCity}</p>
        </div>
      </div>

      {/* Price + book row */}
      <div className="px-5 pb-4 flex items-center gap-3">
        <div className="flex-1">
          <p className="text-[28px] font-black text-emerald-600 tabular-nums leading-none">{arPrice(trip.price)}</p>
          <p className="text-[12px] text-gray-600 font-semibold mt-0.5">
            × {arNum(searchedSeats)} = <span className="font-black text-gray-800">{arPrice(parseFloat(trip.price) * searchedSeats)} ج.س</span>
          </p>
        </div>
        <button
          onClick={() => navigate(`/book/${trip.id}?seats=${searchedSeats}${bookingTypeQuery(bookingType, groupName)}`)}
          disabled={trip.availableSeats === 0}
          className="bg-[#1E40AF] hover:bg-[#1D4ED8] active:scale-[.97] disabled:bg-gray-200 disabled:cursor-not-allowed text-white px-7 py-3.5 rounded-xl text-[15px] font-black transition-all shadow-[0_4px_14px_rgba(30,64,175,.3)] disabled:shadow-none flex-shrink-0"
        >
          {trip.availableSeats === 0 ? 'مكتمل' : 'احجز الآن'}
        </button>
      </div>

      {/* Remarks */}
      {trip.remarks && (
        <div className="px-5 pb-3">
          <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-xl px-3 py-2">
            <svg className="w-3.5 h-3.5 text-blue-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
            </svg>
            <p className="text-xs font-semibold text-blue-800 leading-relaxed">{trip.remarks}</p>
          </div>
        </div>
      )}

      {/* Amenities + seat fill bar */}
      <div className="px-5 pb-4 flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-1.5 flex-wrap">
          {trip.bus?.hasAC       && <span className="bg-cyan-50   text-cyan-700   text-xs px-2.5 py-1 rounded-full border border-cyan-100 inline-flex items-center gap-1"><Snowflake size={11} strokeWidth={2} /> تكييف</span>}
          {trip.bus?.hasWifi     && <span className="bg-indigo-50 text-indigo-700 text-xs px-2.5 py-1 rounded-full border border-indigo-100 inline-flex items-center gap-1"><Wifi size={11} strokeWidth={2} /> واي فاي</span>}
          {trip.bus?.hasScreens  && <span className="bg-purple-50 text-purple-700 text-xs px-2.5 py-1 rounded-full border border-purple-100 inline-flex items-center gap-1"><Tv size={11} strokeWidth={2} /> شاشات</span>}
          {trip.bus?.hasChargers && <span className="bg-yellow-50 text-yellow-700 text-xs px-2.5 py-1 rounded-full border border-yellow-100 inline-flex items-center gap-1"><BatteryCharging size={11} strokeWidth={2} /> شحن</span>}
          <span className={`text-xs px-2.5 py-1 rounded-full border inline-flex items-center gap-1 ${partial ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-gray-50 text-gray-700 border-gray-200'}`}>
            <Armchair size={11} strokeWidth={2} /> {arNum(trip.availableSeats)} متاح
          </span>
        </div>
        <div className="flex items-center gap-2 min-w-[100px]">
          <div className="flex-1 bg-gray-100 rounded-full h-1.5">
            <div
              className={`h-1.5 rounded-full transition-all ${pct >= 80 ? 'bg-red-400' : pct >= 50 ? 'bg-yellow-400' : 'bg-emerald-400'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="text-xs text-gray-600 font-semibold w-8 text-left tabular-nums">{arNum(pct)}%</span>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   RoundTripCard
═══════════════════════════════════════════════════════════ */
const RoundTripCard = ({ pair, searchedSeats, navigate, bookingType, groupName }) => {
  const { out, ret, sameCompany } = pair;
  const total       = (out ? parseFloat(out.price) : 0) + (ret ? parseFloat(ret.price) : 0);
  const grandTotal  = total * searchedSeats;
  const canBookBoth = out && ret && out.availableSeats > 0 && ret.availableSeats > 0;
  const outPartial  = out && out.availableSeats > 0 && out.availableSeats < searchedSeats;
  const retPartial  = ret && ret.availableSeats > 0 && ret.availableSeats < searchedSeats;
  const anyPartial  = outPartial || retPartial;

  const fmt  = (dt) => new Date(dt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
  const fmtD = (dt) => new Date(dt).toLocaleDateString('ar-SA', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });

  const Row = ({ trip, label }) => !trip ? (
    <div className="flex items-center gap-3 py-3 px-1 opacity-40">
      <span className="text-xs font-bold text-gray-400 w-16 flex-shrink-0">{label}</span>
      <span className="text-xs text-gray-400">غير متاح</span>
    </div>
  ) : (
    <div className="flex items-center gap-3 py-3 px-1">
      <span className="text-xs font-bold text-gray-500 w-16 flex-shrink-0">{label}</span>
      <div className="flex items-center gap-2 flex-1 flex-wrap">
        <span className="font-bold text-gray-800 text-sm tabular-nums">{arNum(fmt(trip.departureTime))}</span>
        <span className="text-gray-600 text-xs font-medium">{trip.fromCity}</span>
        <span className="text-gray-400 text-xs">←</span>
        <span className="font-bold text-gray-800 text-sm tabular-nums">{arNum(fmt(trip.arrivalTime))}</span>
        <span className="text-gray-600 text-xs font-medium">{trip.toCity}</span>
        <span className="text-gray-600 text-xs flex-shrink-0">{fmtD(trip.departureTime)}</span>
      </div>
      <span className="font-bold text-emerald-600 text-sm flex-shrink-0 tabular-nums">{arNum(parseFloat(trip.price).toFixed(0))} ج</span>
    </div>
  );

  return (
    <div
      className={`bg-white rounded-2xl border overflow-hidden transition-all hover:shadow-[0_8px_28px_rgba(10,77,157,.10)] hover:-translate-y-0.5 ${
        anyPartial ? 'border-amber-200' : sameCompany ? 'border-blue-100 shadow-[0_2px_10px_rgba(10,77,157,.06)]' : 'border-gray-100 shadow-[0_2px_10px_rgba(10,77,157,.04)]'
      }`}
      style={{ animation: 'cardSlideIn 0.2s ease both' }}
    >
      <div className={`h-1 ${anyPartial ? 'bg-amber-400' : 'bg-gradient-to-l from-[#1E3A8A] to-[#1E40AF]'}`} />

      <div className={`px-5 py-3 border-b flex items-center justify-between ${sameCompany ? 'bg-blue-50 border-blue-100' : 'bg-gray-50 border-gray-100'}`}>
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-blue-700">{out?.company?.name || ret?.company?.name}</span>
          <VehicleTypeBadge vehicleType={out?.bus?.vehicleType || ret?.bus?.vehicleType} />
        </div>
        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${sameCompany ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-500'}`}>
          {sameCompany ? 'نفس الشركة' : 'شركتان مختلفتان'}
        </span>
      </div>

      {anyPartial && (
        <div className="px-5 pt-3 space-y-1">
          {outPartial && <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-700 flex items-center gap-2"><AlertTriangle size={14} strokeWidth={2} className="flex-shrink-0" /> الذهاب: {arNum(out.availableSeats)} مقعد متاح فقط</div>}
          {retPartial && <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-700 flex items-center gap-2"><AlertTriangle size={14} strokeWidth={2} className="flex-shrink-0" /> الإياب: {arNum(ret.availableSeats)} مقعد متاح فقط</div>}
        </div>
      )}

      <div className="px-4 divide-y divide-gray-50">
        <Row trip={out} label="الذهاب" />
        <Row trip={ret} label="الإياب" />
      </div>

      <div className="px-5 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs text-gray-600 font-semibold">إجمالي الرحلة المزدوجة</p>
          <p className="text-xl font-black text-emerald-600 tabular-nums">
            {arNum(total.toFixed(0))}
            <span className="text-sm font-semibold text-gray-600"> جنيه/مقعد</span>
          </p>
          {searchedSeats > 1 && (
            <p className="text-xs text-gray-600 font-semibold mt-0.5">
              {arNum(searchedSeats)} × {arNum(total.toFixed(0))} = <strong className="text-emerald-600">{arNum(grandTotal.toFixed(0))} ج</strong>
            </p>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {canBookBoth && (
            <button
              onClick={() => navigate(`/book/${out.id}?returnTripId=${ret.id}&seats=${searchedSeats}${bookingTypeQuery(bookingType, groupName)}`)}
              className="bg-[#1E40AF] hover:bg-[#1D4ED8] text-white px-5 py-2.5 rounded-xl text-sm font-bold transition-all shadow-sm"
            >
              احجز الرحلتين
            </button>
          )}
          {!canBookBoth && out && out.availableSeats > 0 && (
            <button onClick={() => navigate(`/book/${out.id}?seats=${searchedSeats}${bookingTypeQuery(bookingType, groupName)}`)}
              className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-4 py-2 rounded-xl text-sm font-semibold transition-all">
              حجز الذهاب فقط
            </button>
          )}
          {!canBookBoth && ret && ret.availableSeats > 0 && (
            <button onClick={() => navigate(`/book/${ret.id}?seats=${searchedSeats}${bookingTypeQuery(bookingType, groupName)}`)}
              className="bg-emerald-100 hover:bg-emerald-200 text-emerald-700 px-4 py-2 rounded-xl text-sm font-semibold transition-all">
              حجز الإياب فقط
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   WelcomeBanner — first-launch consent sheet (once only)
   Stored in localStorage key: safer_terms_accepted
═══════════════════════════════════════════════════════════ */
const WelcomeBanner = () => {
  const [visible, setVisible] = useState(() => !localStorage.getItem('safer_terms_accepted'));
  const [modal, setModal] = useState(null);

  const accept = () => {
    localStorage.setItem('safer_terms_accepted', '1');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <>
      {/* Backdrop (mobile only — doesn't block desktop fully) */}
      <div className="md:hidden fixed inset-0 z-[120] bg-black/30 backdrop-blur-[1px]" />

      {/* Sheet */}
      <div
        dir="rtl"
        className="fixed bottom-0 inset-x-0 z-[130] bg-white rounded-t-[28px] shadow-2xl md:max-w-md md:mx-auto md:rounded-2xl md:bottom-6 md:inset-x-auto md:right-6"
        style={{ animation: 'slideDown 0.3s ease both', paddingBottom: 'max(20px, env(safe-area-inset-bottom))' }}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1 md:hidden">
          <div className="w-10 h-1 bg-gray-200 rounded-full" />
        </div>

        {/* Bus icon + headline */}
        <div className="px-6 pt-4 pb-3 text-center">
          <img src="/logo.png" alt="سافر" className="w-32 h-32 object-cover rounded-[22%] mx-auto mb-3" />
          <h2 className="text-lg font-black text-gray-900">مرحباً بك في سافر!</h2>
          <p className="text-sm text-gray-500 mt-1 leading-relaxed">
            لاستخدام الخدمة، يُرجى الموافقة على الشروط وسياسات الاستخدام.
          </p>
        </div>

        {/* Legal links */}
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 px-6 pb-4">
          {[
            { key: 'terms',   label: 'الشروط والأحكام' },
            { key: 'privacy', label: 'سياسة الخصوصية' },
            { key: 'refund',  label: 'سياسة الإلغاء' },
          ].map(({ key, label }) => (
            <button key={key} onClick={() => setModal(key)}
              className="text-xs text-[#1E40AF] underline underline-offset-2 font-semibold hover:text-[#1E3A8A] transition-colors">
              {label}
            </button>
          ))}
        </div>

        {/* Actions */}
        <div className="px-6 pb-2 flex gap-3">
          <button onClick={() => setVisible(false)}
            className="flex-1 py-3 border border-gray-200 text-gray-500 font-semibold rounded-2xl text-sm hover:bg-gray-50 transition-colors">
            تصفح فقط
          </button>
          <button onClick={accept}
            className="flex-2 flex-grow-[2] py-3 bg-gradient-to-l from-[#1E3A8A] to-[#1E40AF] text-white font-black rounded-2xl text-sm shadow-[0_4px_14px_rgba(30,64,175,.35)] hover:opacity-90 transition-opacity">
            أوافق وأكمل
          </button>
        </div>
      </div>

      {/* Modals */}
      {modal === 'terms'   && <TermsModal         onClose={() => setModal(null)} onAccept={accept} acceptLabel="أوافق" />}
      {modal === 'privacy' && <PrivacyPolicyModal  onClose={() => setModal(null)} />}
      {modal === 'refund'  && <RefundPolicyModal   onClose={() => setModal(null)} />}
    </>
  );
};

/* ═══════════════════════════════════════════════════════════
   PromotionsSection — logged-in: promo codes; guest: login CTA
═══════════════════════════════════════════════════════════ */
const BADGE_CLS = {
  exclusive: 'bg-amber-50 text-amber-700 border-amber-200',
  limited:   'bg-red-50 text-red-600 border-red-200',
  permanent: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};
const BADGE_LABEL = { exclusive: 'حصري', limited: 'محدود', permanent: 'دائم' };

const PromotionsSection = ({ isLoggedIn, navigate, promos = [] }) => {
  const [copied, setCopied] = useState(null);
  const copyCode = (code) => {
    navigator.clipboard?.writeText(code).catch(() => {});
    localStorage.setItem('safer_pending_promo', code);
    setCopied(code);
    setTimeout(() => setCopied(null), 2000);
  };

  if (!isLoggedIn) {
    return (
      <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_2px_8px_rgba(17,24,39,.05)] p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-5">
        <div className="w-16 h-16 rounded-2xl bg-[#EEF2FF] border border-[#C7D2FE] flex items-center justify-center flex-shrink-0 text-[#1E40AF] mx-auto sm:mx-0"><Gift size={28} strokeWidth={2} /></div>
        <div className="flex-1 text-center sm:text-right">
          <h3 className="font-black text-gray-800 text-lg">احصل على عروض وخصومات حصرية</h3>
          <p className="text-gray-500 text-sm mt-1 leading-relaxed">سجّل دخولك للوصول إلى عروض مخصصة وكوبونات خصم على بعض رحلاتك</p>
        </div>
        <div className="flex gap-3 flex-shrink-0 flex-col sm:flex-row w-full sm:w-auto">
          <button
            onClick={() => navigate('/login')}
            className="bg-[#1E40AF] hover:bg-[#1D4ED8] text-white font-bold px-5 py-2.5 rounded-xl transition-all text-sm text-center"
          >
            تسجيل الدخول
          </button>
          <button className="border border-[#E5E7EB] text-[#6B7280] hover:border-[#1E40AF]/40 hover:text-[#1E40AF] font-semibold px-5 py-2.5 rounded-xl transition-all text-sm text-center">
            تصفح كزائر
          </button>
        </div>
      </div>
    );
  }

  if (promos.length === 0) return null;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-black text-gray-800">عروض وخصومات</h2>
          <p className="text-sm text-gray-400 mt-0.5">كوبونات الخصم المتاحة على رحلاتك</p>
        </div>
      </div>

      {/* Tip banner */}
      <div className="mb-4 flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
        <Lightbulb size={20} strokeWidth={2} className="text-blue-600 flex-shrink-0" />
        <p className="text-sm text-blue-800 leading-relaxed">
          انقر على <span className="font-bold">نسخ</span> بجانب الكود — سيُطبَّق تلقائياً على حجزك التالي بمجرد الانتقال لصفحة الحجز.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {promos.map((promo) => (
          <div key={promo.code} className="bg-white rounded-2xl border border-[#E5E7EB] p-5 hover:shadow-[0_6px_24px_rgba(30,64,175,.10)] hover:-translate-y-0.5 transition-all">
            <div className="flex items-start justify-between mb-3">
              <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${BADGE_CLS[promo.badge] || BADGE_CLS.exclusive}`}>
                {BADGE_LABEL[promo.badge] || promo.badge}
              </span>
              {promo.validUntil && <span className="text-xs text-gray-400">ينتهي {promo.validUntil}</span>}
            </div>
            <p className="font-bold text-gray-800 text-sm leading-snug">{promo.title}</p>
            {promo.company && <p className="text-xs text-gray-400 mt-1">{promo.company.name}</p>}
            <div className="mt-3 bg-[#EEF2FF] border border-[#C7D2FE] rounded-xl px-3 py-2 flex items-center justify-between gap-2">
              <span className="font-bold text-[#1E3A8A] text-sm tracking-widest" dir="ltr">{promo.code}</span>
              <button
                onClick={() => copyCode(promo.code)}
                className="text-xs text-[#1E40AF] hover:text-[#1E3A8A] font-bold transition-colors flex-shrink-0"
              >
                {copied === promo.code ? '✓ تم النسخ' : 'نسخ'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   PassengerSearchPage — main export
═══════════════════════════════════════════════════════════ */
const PassengerSearchPage = () => {
  const navigate   = useNavigate();
  const user       = JSON.parse(localStorage.getItem('passenger_user') || '{}');
  const isLoggedIn = !!user?.id;

  // مشاوير/طرود/نقل عام require a real account — unlike bus-ticket booking,
  // which stays open to guests. Redirects to /login instead of the target.
  const navigateToService = (target) => navigate(isLoggedIn ? target : '/login');

  const [tripType,        setTripType]        = useState('one-way');
  const [form,            setForm]            = useState({ fromCity: '', toCity: '', date: '', returnDate: '', hasAC: false, hasWifi: false, hasChargers: false, hasTV: false, seats: 1, vehicleTypeId: null, bookingType: 'individual', groupName: '' });
  const [groupSuggestions, setGroupSuggestions] = useState(null); // { vehicleSuggestions, tripsWithCapacity, alternatives }
  const [waitlistJoined,   setWaitlistJoined]   = useState(false);
  const [activeService,   setActiveService]   = useState(null); // حجز رحلة / مشاوير / إرسال طرد / نقل عام tab — none pre-selected; a tab only lights up once the user actually picks it
  const searchCardRef = useRef(null);
  const [cities,          setCities]          = useState([]);
  const [citiesLoading,   setCitiesLoading]   = useState(true);
  const [outboundTrips,   setOutboundTrips]   = useState([]);
  const [returnTrips,     setReturnTrips]     = useState([]);
  const [roundPairs,      setRoundPairs]      = useState([]);
  const [searched,        setSearched]        = useState(false);
  const [searchCollapsed, setSearchCollapsed] = useState(false);
  const [loading,         setLoading]         = useState(false);
  const [sortBy,          setSortBy]          = useState('time');
  const [searchedSeats,   setSearchedSeats]   = useState(1);
  const [popularRoutes,   setPopularRoutes]   = useState([]);
  const [promos,          setPromos]          = useState([]);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [bannerCopied,    setBannerCopied]    = useState(null);
  const [searchError,     setSearchError]     = useState('');
  const [infoService,     setInfoService]     = useState(null); // خدمات سافر / تذكرتك card the info popup is currently showing

  useEffect(() => {
    tripAPI.getCities()
      .then(res => setCities(res.data.data || []))
      .catch(() => {})
      .finally(() => setCitiesLoading(false));
    popularRouteAPI.getAll(false)
      .then(res => setPopularRoutes(res.data.data || []))
      .catch(() => {});
    promoAPI.getAll()
      .then(res => setPromos(res.data.data || []))
      .catch(() => {});
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    setSearchError('');

    // Both fields blank means "browse everything" (an intentional existing
    // capability — the collapsed-summary bar already renders 'أي مدينة' for
    // that case), so only a same-city pair is actually invalid here.
    if (form.fromCity && form.toCity && form.fromCity === form.toCity) {
      setSearchError('مدينة المغادرة والوصول لا يمكن أن تكونا متطابقتين');
      return;
    }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setSearchError('لا يوجد اتصال بالإنترنت. تحقق من الشبكة وحاول مرة أخرى.');
      return;
    }

    setSearchedSeats(form.seats);
    setGroupSuggestions(null); setWaitlistJoined(false);
    setLoading(true);
    try {
      const base = {};
      if (form.fromCity)    base.fromCity    = form.fromCity;
      if (form.toCity)      base.toCity      = form.toCity;
      if (form.date)        base.date        = form.date;
      if (form.hasAC)       base.hasAC       = true;
      if (form.hasWifi)     base.hasWifi     = true;
      if (form.hasChargers) base.hasChargers = true;
      if (form.vehicleTypeId) base.vehicleTypeId = form.vehicleTypeId;

      let outs = [];
      if (tripType === 'one-way') {
        const res = await tripAPI.search(base);
        outs = res.data.data;
        setOutboundTrips(outs);
        setReturnTrips([]); setRoundPairs([]);
      } else {
        const returnBase = { ...base, fromCity: form.toCity, toCity: form.fromCity };
        if (form.returnDate) returnBase.date = form.returnDate;
        const [outRes, retRes] = await Promise.all([
          tripAPI.search(base),
          tripAPI.search(returnBase),
        ]);
        const rets = retRes.data.data; outs = outRes.data.data;
        setOutboundTrips(outs); setReturnTrips(rets); setRoundPairs(pairTrips(outs, rets));
      }
      setSearched(true);
      setSearchCollapsed(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Smart Group Transportation System (Phase 1) — if this is a family/group
      // search and no result trip has enough seats together, surface vehicle
      // suggestions, alternative trips, and a waiting-list option.
      if (form.bookingType !== 'individual' && form.fromCity && form.toCity) {
        const hasEnough = outs.some(tr => tr.availableSeats >= form.seats);
        if (!hasEnough) {
          try {
            const sugRes = await tripAPI.getGroupSuggestions({
              fromCity: form.fromCity, toCity: form.toCity, date: form.date || undefined, passengerCount: form.seats,
            });
            setGroupSuggestions(sugRes.data.data);
          } catch { /* non-critical — search results already shown */ }
        }
      }
    } catch {
      // Left un-searched (card stays expanded with the error banner) rather
      // than marking searched=true with empty results — that would render
      // the misleading "no trips found" empty state for what's actually a
      // failed request, not a real zero-result search.
      setOutboundTrips([]); setReturnTrips([]); setRoundPairs([]);
      const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
      setSearchError(offline
        ? 'لا يوجد اتصال بالإنترنت. تحقق من الشبكة وحاول مرة أخرى.'
        : 'تعذر تحميل الرحلات. حاول مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinWaitlist = async () => {
    if (!isLoggedIn) { navigate('/login'); return; }
    try {
      await waitingListAPI.join({
        fromCity: form.fromCity,
        toCity: form.toCity,
        date: form.date || new Date().toISOString().split('T')[0],
        passengerCount: form.seats,
      });
      setWaitlistJoined(true);
    } catch { /* non-critical — user can retry */ }
  };

  const hasEnoughSeats = (trip) => trip && trip.availableSeats >= searchedSeats;
  const hasResults = tripType === 'one-way' ? outboundTrips.length > 0 : roundPairs.length > 0;
  // Picking حجز رحلة collapses straight to a full trip-booking page — hero,
  // service grid, promos etc. all drop away — matching how the other 3
  // service tabs each navigate to their own dedicated, form-only page.
  const showHero   = !searched && activeService !== 'trip';

  const sortedOutbound = [...outboundTrips].sort((a, b) => {
    const aE = hasEnoughSeats(a) ? 1 : 0; const bE = hasEnoughSeats(b) ? 1 : 0;
    if (bE !== aE) return bE - aE;
    return sortBy === 'price' ? parseFloat(a.price) - parseFloat(b.price) : new Date(a.departureTime) - new Date(b.departureTime);
  });

  const sortedPairs = [...roundPairs].sort((a, b) => {
    const aE = hasEnoughSeats(a.out) && hasEnoughSeats(a.ret) ? 1 : 0;
    const bE = hasEnoughSeats(b.out) && hasEnoughSeats(b.ret) ? 1 : 0;
    if (bE !== aE) return bE - aE;
    if (sortBy === 'price') {
      const aT = (a.out ? parseFloat(a.out.price) : 0) + (a.ret ? parseFloat(a.ret.price) : 0);
      const bT = (b.out ? parseFloat(b.out.price) : 0) + (b.ret ? parseFloat(b.ret.price) : 0);
      return aT - bT;
    }
    return 0;
  });


  const applyBannerPromo = (code) => {
    localStorage.setItem('safer_pending_promo', code);
    setBannerCopied(code);
    setTimeout(() => setBannerCopied(null), 2500);
  };

  return (
    <div dir="rtl" className="min-h-screen bg-[#F5F9FF] pb-20 md:pb-0">
      <AppNavbar />

      {/* ════════════════════ Promo notification banner ════════════════════ */}
      {promos.length > 0 && !bannerDismissed && (
        <div className="bg-gradient-to-l from-blue-700 to-blue-800 text-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap">
            {!isLoggedIn ? (
              <>
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Gift size={18} strokeWidth={2} className="flex-shrink-0" />
                  <span>هناك عروض وخصومات متاحة على بعض الرحلات — سجّل دخولك للاستفادة منها</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => navigate('/login')}
                    className="bg-white text-blue-700 hover:bg-blue-50 text-xs font-black px-3 py-1.5 rounded-lg transition-colors"
                  >
                    تسجيل الدخول
                  </button>
                  <button onClick={() => setBannerDismissed(true)} className="text-blue-200 hover:text-white text-xl leading-none px-1">×</button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 flex-wrap">
                  <Tag size={18} strokeWidth={2} className="flex-shrink-0" />
                  <span className="text-sm font-medium">خصومات متاحة على رحلاتك — انقر على الكود لتطبيقه تلقائياً عند الحجز:</span>
                  {promos.slice(0, 3).map(p => (
                    <button
                      key={p.code}
                      onClick={() => applyBannerPromo(p.code)}
                      className="bg-white/15 hover:bg-white/25 border border-white/40 text-xs font-black px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5"
                      dir="ltr"
                    >
                      {bannerCopied === p.code
                        ? <span className="text-green-300">✓ سيُطبق عند الحجز</span>
                        : <><span>{p.code}</span><span className="text-blue-200">{p.discountPercent}%</span></>
                      }
                    </button>
                  ))}
                </div>
                <button onClick={() => setBannerDismissed(true)} className="text-blue-200 hover:text-white text-xl leading-none px-1 flex-shrink-0">×</button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ════════════════════ Hero + booking (merged — no more separate
           floating card overlapping the hero via negative margin) ════════════════════ */}
      {showHero ? (
        <div ref={searchCardRef} className="scroll-mt-[88px]">
          <HeroSection>
            <ServiceTabBar
              active={activeService}
              onChange={(key) => (key === 'trip' ? setActiveService('trip') : navigateToService(SERVICE_TARGETS[key]))}
            />
            <SearchCard
              narrow
              tripType={tripType} setTripType={setTripType}
              form={form} setForm={setForm}
              cities={cities} citiesLoading={citiesLoading}
              loading={loading} onSearch={handleSearch} searchError={searchError}
              searched={searched}
              searchCollapsed={searchCollapsed} setSearchCollapsed={setSearchCollapsed}
              searchedSeats={searchedSeats}
              outboundTrips={outboundTrips} roundPairs={roundPairs}
            />
            <div className="flex justify-center lg:justify-start mt-5">
              <button
                onClick={() => document.getElementById('safer-services')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                className="flex items-center gap-2 text-sm font-bold text-[#1E40AF] hover:text-[#1E3A8A] bg-white border border-gray-200 hover:border-blue-200 rounded-full px-5 py-2.5 shadow-sm transition-all"
              >
                اكتشف جميع الخدمات
                <ChevronLeft size={16} strokeWidth={2.5} />
              </button>
            </div>
          </HeroSection>
        </div>
      ) : (
        <div ref={searchCardRef} className="scroll-mt-[88px] px-4 sm:px-6 sticky top-[68px] z-40 bg-[#F5F9FF]/95 backdrop-blur-sm border-b border-[#E5E7EB] shadow-sm py-3">
          <div className="max-w-7xl mx-auto">
            <SearchCard
              tripType={tripType} setTripType={setTripType}
              form={form} setForm={setForm}
              cities={cities} citiesLoading={citiesLoading}
              loading={loading} onSearch={handleSearch} searchError={searchError}
              searched={searched}
              searchCollapsed={searchCollapsed} setSearchCollapsed={setSearchCollapsed}
              searchedSeats={searchedSeats}
              outboundTrips={outboundTrips} roundPairs={roundPairs}
            />
          </div>
        </div>
      )}

      {/* ════════════════════ Pre-search sections ════════════════════ */}
      {showHero && (
        <div className="max-w-6xl mx-auto px-4 sm:px-8 mt-8 sm:mt-10 space-y-8 pb-10">

          {/* خدمات سافر — 4-service grid */}
          <ServicesGridSection onCardClick={setInfoService} />

          {/* تذكرتك الإلكترونية / المساعدة */}
          <BottomFeatureCards onTicketClick={() => setInfoService(TICKET_INFO)} />

          {/* Feature cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            <FeatureCard Icon={Headphones} iconColor="text-emerald-600" title="دعم 24/7"        subtitle="عبر واتساب على مدار الساعة"    accent="bg-emerald-50" />
            <FeatureCard Icon={Zap}        iconColor="text-amber-600"  title="تأكيد فوري"      subtitle="احصل على تذكرتك فوراً"         accent="bg-amber-50" />
            <FeatureCard Icon={Tag}        iconColor="text-purple-600" title="أفضل الأسعار"    subtitle="نضمن لك أفضل العروض"           accent="bg-purple-50" />
            <FeatureCard Icon={Smartphone} iconColor="text-rose-600"   title="تذكرتك معك"      subtitle="في أي وقت ومن أي مكان"         accent="bg-rose-50" />
          </div>

          {/* Promotions section */}
          <PromotionsSection isLoggedIn={isLoggedIn} navigate={navigate} promos={promos} />

          {/* Popular routes */}
          {popularRoutes.length > 0 && (
          <div id="popular-routes" className="scroll-mt-24">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-black text-gray-800">الوجهات الشائعة</h2>
                <p className="text-sm text-gray-600 font-semibold mt-0.5">الرحلات الأكثر حجزاً على منصة سافر</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {popularRoutes.map((r) => (
                <div
                  key={r.id}
                  onClick={() => { setForm(f => ({ ...f, fromCity: r.fromCity, toCity: r.toCity })); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-[0_8px_28px_rgba(10,77,157,.10)] hover:-translate-y-1 transition-all overflow-hidden cursor-pointer"
                >
                  <div className="relative h-28 overflow-hidden flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg,#1E3A8A,#1E40AF)' }}>
                    {r.imageUrl ? (
                      <img
                        src={resolveUrl(r.imageUrl)}
                        alt={`${r.fromCity} → ${r.toCity}`}
                        className="absolute inset-0 w-full h-full object-cover"
                        style={{ filter: 'brightness(0.85) saturate(1.1)' }}
                      />
                    ) : (
                      <>
                        <svg className="absolute inset-0 w-full h-full opacity-[0.12]" viewBox="0 0 300 120" fill="white" preserveAspectRatio="xMidYMax meet">
                          <rect x="10"  y="40" width="20" height="80"/>
                          <rect x="35"  y="20" width="25" height="100"/>
                          <rect x="65"  y="30" width="18" height="90"/>
                          <rect x="88"  y="10" width="22" height="110"/>
                          <rect x="200" y="35" width="20" height="85"/>
                          <rect x="225" y="15" width="28" height="105"/>
                          <rect x="258" y="28" width="18" height="92"/>
                        </svg>
                        <Building2 size={40} strokeWidth={1.5} className="relative z-10 text-white/70" />
                      </>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 text-sm font-bold text-gray-800">
                      <span>{r.fromCity}</span>
                      <span className="text-[#1E88E5]">→</span>
                      <span>{r.toCity}</span>
                    </div>
                    <div className="flex items-center justify-between mt-2 text-xs text-gray-600 font-semibold">
                      <span className="flex items-center gap-1"><Clock size={12} strokeWidth={2} /> {r.minHours} - {r.maxHours} ساعات</span>
                      <span className="flex items-center gap-1"><BusFront size={12} strokeWidth={2} /> {arNum(r.dailyTrips)} رحلة يومية</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          )}

        </div>
      )}

      {/* ════════════════════ Search results — full page ════════════════════ */}
      {(searched || loading) && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex gap-6 items-start">

            {/* ── Left sidebar: sort + trip info (desktop only) ── */}
            <aside className="hidden lg:flex flex-col gap-4 w-56 flex-shrink-0 sticky top-[140px]">
              <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-sm p-4">
                <p className="text-xs font-black text-gray-500 uppercase tracking-widest mb-3">ترتيب حسب</p>
                <div className="flex flex-col gap-2">
                  {[['time', Clock, 'الموعد', 'الأقرب وقتاً'], ['price', Wallet, 'السعر', 'الأقل تكلفة']].map(([val, Icon, label, sub]) => (
                    <button key={val} onClick={() => setSortBy(val)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-right transition-all w-full ${
                        sortBy === val
                          ? 'bg-[#EEF2FF] border border-[#C7D2FE] text-[#1E40AF]'
                          : 'text-gray-600 hover:bg-gray-50 border border-transparent'
                      }`}>
                      <Icon size={18} strokeWidth={2} className="flex-shrink-0" />
                      <div>
                        <p className={`text-sm font-bold leading-none ${sortBy === val ? 'text-[#1E40AF]' : 'text-gray-700'}`}>{label}</p>
                        <p className="text-[11px] text-gray-600 mt-0.5">{sub}</p>
                      </div>
                      {sortBy === val && (
                        <svg className="w-4 h-4 mr-auto flex-shrink-0 text-[#1E40AF]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quick info box */}
              <div className="bg-[#EEF2FF] border border-[#C7D2FE] rounded-2xl p-4 text-[#1E3A8A]">
                <p className="text-xs font-black uppercase tracking-widest mb-2 text-[#1E40AF]">معلومة</p>
                <p className="text-xs leading-relaxed text-[#1E3A8A]/80">الأسعار تشمل مقعداً واحداً. يمكن اختيار عدد المقاعد في خطوة الحجز.</p>
              </div>
            </aside>

            {/* ── Main results column ── */}
            <div className="flex-1 min-w-0 space-y-4">

              {/* Mobile sort bar */}
              <div className="lg:hidden flex items-center justify-between">
                <p className="text-sm font-bold text-gray-700">
                  {loading ? 'جارٍ البحث...' : tripType === 'one-way' ? `${arNum(outboundTrips.length)} رحلة متاحة` : `${arNum(roundPairs.length)} باقة متاحة`}
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-600 font-semibold">ترتيب:</span>
                  {[['time', Clock, 'الموعد'], ['price', Wallet, 'السعر']].map(([val, Icon, label]) => (
                    <button key={val} onClick={() => setSortBy(val)}
                      className={`text-xs px-3 py-1.5 rounded-full transition-all inline-flex items-center gap-1 ${sortBy === val ? 'bg-[#1E3A8A] text-white' : 'bg-white text-gray-600 border border-gray-200'}`}>
                      <Icon size={12} strokeWidth={2} /> {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Desktop results count */}
              {!loading && (
                <div className="hidden lg:flex items-center justify-between border-b border-[#E5E7EB] pb-3">
                  <p className="text-base font-black text-gray-800">
                    {tripType === 'one-way' ? `${arNum(outboundTrips.length)} رحلة متاحة` : `${arNum(roundPairs.length)} باقة متاحة`}
                  </p>
                  <p className="text-xs text-gray-600 font-semibold">مرتبة حسب: {sortBy === 'time' ? 'الموعد' : 'السعر'}</p>
                </div>
              )}

              {/* Loading skeletons */}
              {loading && (
                <div className="space-y-4">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5 animate-pulse">
                      <div className="flex justify-between mb-4">
                        <div className="h-4 bg-gray-100 rounded w-32" />
                        <div className="h-4 bg-gray-100 rounded w-20" />
                      </div>
                      <div className="flex items-center gap-6 mb-4">
                        <div className="h-10 bg-gray-100 rounded w-20" />
                        <div className="flex-1 h-1 bg-gray-100 rounded-full" />
                        <div className="h-10 bg-gray-100 rounded w-20" />
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="h-8 bg-gray-100 rounded-xl w-32" />
                        <div className="h-12 bg-gray-100 rounded-xl w-28" />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Smart Group Transportation System (Phase 1) — insufficient-capacity
                  panel: vehicle suggestions, alternative trips, waiting list */}
              {!loading && groupSuggestions && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 space-y-4">
                  <div className="flex items-center gap-2">
                    <Lightbulb size={20} strokeWidth={2} className="text-amber-600 flex-shrink-0" />
                    <p className="font-bold text-amber-800 text-sm">
                      لا توجد رحلة واحدة تتسع لـ {arNum(form.seats)} راكباً معاً — إليك بعض الخيارات
                    </p>
                  </div>

                  {groupSuggestions.vehicleSuggestions?.length > 0 && (
                    <div>
                      <p className="text-xs font-bold text-gray-600 mb-1.5">المركبة المقترحة لعدد ركابكم:</p>
                      <div className="flex gap-2 flex-wrap">
                        {groupSuggestions.vehicleSuggestions.map((v, i) => (
                          <span key={v.id || i} className="inline-flex items-center gap-1.5 bg-white border border-amber-200 text-amber-800 text-xs font-semibold px-3 py-1.5 rounded-full">
                            {v.icon || <CarFront size={13} strokeWidth={2} />} {v.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {groupSuggestions.alternatives?.length > 0 && (
                    <div>
                      <p className="text-xs font-bold text-gray-600 mb-1.5">رحلات بديلة تتسع لمجموعتكم:</p>
                      <div className="space-y-2">
                        {groupSuggestions.alternatives.slice(0, 5).map(trip => (
                          <div key={trip.id} className="flex items-center justify-between bg-white border border-amber-100 rounded-xl px-4 py-2.5 gap-3 flex-wrap">
                            <div className="text-sm">
                              <span className="font-bold text-gray-800">{trip.fromCity} ← {trip.toCity}</span>
                              <span className="text-gray-600 mx-2 text-xs">
                                {new Date(trip.departureTime).toLocaleDateString('ar-SA', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' })}
                              </span>
                              <span className="text-emerald-700 text-xs font-semibold inline-flex items-center gap-1"><Armchair size={12} strokeWidth={2} /> {arNum(trip.availableSeats)} متاح</span>
                            </div>
                            <button
                              onClick={() => navigate(`/book/${trip.id}?seats=${form.seats}${bookingTypeQuery(form.bookingType, form.groupName)}`)}
                              className="bg-[#1E40AF] hover:bg-[#1D4ED8] text-white text-xs font-bold px-4 py-2 rounded-lg flex-shrink-0"
                            >
                              احجز
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between bg-white border border-amber-100 rounded-xl px-4 py-3 gap-3 flex-wrap">
                    <p className="text-xs text-gray-700 font-semibold">
                      لم تجد ما يناسبك؟ سجّل في قائمة الانتظار وسنبلغك فور توفر مقاعد كافية.
                    </p>
                    {waitlistJoined ? (
                      <span className="text-emerald-700 text-xs font-bold flex-shrink-0 inline-flex items-center gap-1"><CheckCircle2 size={14} strokeWidth={2} /> تم التسجيل</span>
                    ) : (
                      <button onClick={handleJoinWaitlist} className="bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold px-4 py-2 rounded-lg flex-shrink-0">
                        {isLoggedIn ? 'انضم لقائمة الانتظار' : 'سجّل الدخول للانضمام'}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* No results */}
              {!loading && searched && !hasResults && (
                <div className="text-center bg-white rounded-2xl shadow-sm border border-gray-100 py-20">
                  <Search size={56} strokeWidth={1.5} className="mx-auto mb-4 opacity-40 text-gray-500" />
                  <p className="font-bold text-gray-700 text-xl">لا توجد رحلات متاحة لهذا المسار في التاريخ المحدد</p>
                  <p className="text-gray-600 font-semibold text-sm mt-2">جرّب تاريخاً آخر أو وجهة مختلفة</p>
                  <button
                    onClick={() => { setSearchCollapsed(false); searchCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                    className="mt-5 bg-blue-50 hover:bg-blue-100 text-[#1E3A8A] text-sm font-bold px-5 py-2.5 rounded-xl border border-blue-100 transition-colors"
                  >
                    تعديل البحث
                  </button>
                </div>
              )}

              {/* One-way trip cards */}
              {!loading && tripType === 'one-way' && sortedOutbound.length > 0 && (
                <div className="space-y-4">
                  {sortedOutbound.map(trip => (
                    <TripCard key={trip.id} trip={trip} searchedSeats={searchedSeats} navigate={navigate} bookingType={form.bookingType} groupName={form.groupName} />
                  ))}
                </div>
              )}

              {/* Round-trip cards */}
              {!loading && tripType === 'round-trip' && sortedPairs.length > 0 && (
                <>
                  <div className="flex items-center gap-2 text-xs text-gray-600 font-semibold">
                    <span>{arNum(outboundTrips.length)} ذهاب</span>
                    <span>·</span>
                    <span>{arNum(returnTrips.length)} إياب</span>
                  </div>
                  <div className="space-y-4">
                    {sortedPairs.map((pair, i) => (
                      <RoundTripCard key={i} pair={pair} searchedSeats={searchedSeats} navigate={navigate} bookingType={form.bookingType} groupName={form.groupName} />
                    ))}
                  </div>
                </>
              )}

            </div>
          </div>
        </div>
      )}

      {/* ════════════════════ Site footer (desktop) ════════════════════ */}
      <div className="hidden md:block mt-12">
        <SiteFooter navigate={navigate} />
      </div>

      {/* ════════════════════ Mobile bottom nav ════════════════════ */}
      <MobileBottomNav />

      {/* ════════════════════ First-launch consent banner ════════════════════ */}
      <WelcomeBanner />

      {/* ════════════════════ Service info popup (خدمات سافر / تذكرتك cards) ════════════════════ */}
      <ServiceInfoModal
        info={infoService}
        onClose={() => setInfoService(null)}
        onContinue={() => {
          const s = infoService;
          setInfoService(null);
          if (s.target) navigateToService(s.target); else setActiveService('trip');
        }}
      />
    </div>
  );
};

export default PassengerSearchPage;
